import { LitElement, css, html, nothing } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { consume } from '@lit/context'
// import sampleResults from '../../fixtures/results.json' assert { type: 'json' }
import { breakpointContext } from '../../contexts/breakpoint'
import { BREAKPOINT } from '../../util/breakpoints'
import { formatCurrency } from '../../util/format'
import { getCostBreakdownDescription } from '../../util/cost-breakdown'
import { type TemplateResult } from 'lit'

import { classMap } from 'lit/directives/class-map.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { features } from '../../util/plans'
import { shopperContext, type ShopperContextType } from '../../contexts/shopper'
import { ApplicationService } from '../../services/application.service'
import { clearSelections } from '../../util/clear-selections'
import { orderVisionPlans } from '../../util/sort-plans'
import { STORAGE_KEY } from '../../util/storage-keys'
import {
  isReadOnlyFromUrl,
  shouldDisablePlanAction,
} from '../../util/read-only-actions'
import { getPlanYear } from '../../util/pdf-config'
import { withBase } from '../../util/base-path'

interface Feature {
  key: string
  display: string | TemplateResult
  defaultValue?: string | TemplateResult
  list?: string
  compare?: string
  planCode?: string
  planName?: string
  // Backward compatibility for older items using "label"
  label?: string | TemplateResult
}

@customElement('rq-marketplace-compare')
export class MarketplaceCompare extends LitElement {
  @property({ type: Boolean })
  readOnly = false

  @property({ type: Boolean })
  isShopper = true

  @property({ type: Array })
  features: Feature[] = features
  // [
  //   { label: 'Frames', key: 'productType' },
  //   {
  //     label: html`Standard Plastic Lenses Single Vision<sup>1</sup>`,
  //     key: 'header',
  //   },
  //   {
  //     label: html`Standard Plastic Lenses Bifocal<sup>1</sup>`,
  //     key: 'coinsuranceText',
  //   },
  //   {
  //     label: html`Standard Plastic Lenses Lenticular<sup>1</sup>`,
  //     key: 'pricing',
  //   },
  //   {
  //     label: html`Contact Lenses Conventional<sup>1</sup>`,
  //     key: 'primaryCare',
  //   },
  //   { label: html`Contact Lenses Disposable<sup>1</sup>`, key: 'specialist' },
  //   {
  //     label: 'Contact Lenses Medically necessary',
  //     key: 'familyOutOfPocketMax',
  //   },
  //   { label: 'Frequency-Exam', key: 'individualDeductible' },
  //   { label: 'Frequency-Frames', key: 'familyMemberDeductible' },
  //   {
  //     label: 'Family deductibleFrequency-Lenses OR Contact Lenses',
  //     key: 'familyDeductible',
  //   },
  //   { label: 'Routine Eye Exam Dilation', key: 'rxDeductible' },
  // ]

  @consume({ context: shopperContext, subscribe: true })
  @state()
  shopperContextValue?: ShopperContextType

  @consume({ context: breakpointContext, subscribe: true })
  breakpoint!: BREAKPOINT

  static styles = [
    css`
      :host {
        all: unset;
      }
      .table-container {
        overflow-x: auto;
        -webkit-overflow-scrolling: touch;
      }
      .compare-table {
        border-collapse: collapse;
        margin-top: 1rem;
        margin-left: 0;
        margin-right: 0;
        width: 100%;
      }
      .compare-table {
        table-layout: fixed;
      }
      @media (max-width: 768px) {
        .compare-table {
          table-layout: auto;
          min-width: 600px;
        }
      }
      .compare-table th,
      .compare-table td {
        border: 1px solid var(--lh-color-neutral-200, #d1d3d4);
        padding: 0.75rem;
        text-align: left;
        color: var(--Neutral-800, #222425) !important;
        font-family: 'Blue Ridge Sans' !important;
        font-size: 18px !important;
        font-weight: 400 !important;
        line-height: 24px !important;
        vertical-align: top;
      }

      .compare-table td:first-child,
      .compare-table th:first-child {
        background-color: var(--lh-color-primary-50, #ebf9ff);
        font-weight: bold;
        width: 25%;
      }
      @media (max-width: 992px) {
        .compare-table th:not(:first-child) {
          width: 25.33% !important;
        }
      }
      .compare-table th:not(:first-child) {
        width: 33.33%;
        padding: 0;
      }
      .action-buttons {
        display: flex;
        justify-content: flex-end;
        margin-top: 1.5rem;
        gap: 1rem;
      }
      .subscript {
        font-size: 1.2rem;
        color: var(--Neutral-800, #222425);
      }
      .stars {
        color: var(--lh-color-primary-500, #00aeef);
      }
      .min-height-130 {
        min-height: 8.125rem;
      }
      .min-height-100 {
        min-height: 7rem;
      }
      .px-25px {
        padding: 0 25px;
      }
      .justify-content-start {
        display: flex;
        justify-content: flex-start;
      }
      .align-items-4 {
        display: inline-flex;
        align-items: center;
        gap: 4px;
      }
      ul {
        margin-left: -10px;
        list-style-type: disc;
      }

      lh-grid-item {
        padding: 0;
        margin: 0;
        gap: 0;
      }
      .compare-table th {
        height: 100%;
        vertical-align: top;
      }

      .grid-container {
        flex: 1 1 auto;
        display: grid;
        grid-template-rows:
          minmax(6rem, auto) minmax(3.5rem, auto)
          auto;
        gap: 1rem;
        padding: 7%;
      }

      /* .grid-container > .grid-item:first-child {
        margin-bottom: -1rem !important;
      } */
      /* .grid-container.is-shopper-false {
        width: 84%;
        padding: 8%;
      }
      .compare-table th:not(:first-child) {
        padding: 0;
      }

      .margin-bottom-last-child {
        margin-bottom: -1rem;
      } */

      .plan-header-row {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
      }

      .grid-item {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        min-height: 0;
      }

      .price {
        display: flex;
        flex-direction: column;
        width: 100%;
      }

      .costs {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .price-buttons {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .sticky-buttons {
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        margin-top: auto;
        width: 100%;
      }
      .header-mobile {
        padding-left: 16px;
        padding-right: 16px;
      }
      .pt-15 {
        padding-top: 15px;
      }
      .pt-52 {
        padding-top: 3.25rem;
      }
      .height-3rem {
        height: 3.125rem;
      }
    `,
  ]
  private brochureLink: string = ''
  private baseUrl: string = ''
  connectedCallback(): void {
    super.connectedCallback()
    if (typeof window !== 'undefined') {
      if (
        window.location.host.includes('shopperstage.bcbsnc.com') ||
        window.location.host.includes('localhost')
      ) {
        this.baseUrl = 'https://assetsps.bcbsnc.com/assets/email'
      } else if (window.location.host.includes('shopper.bcbsnc.com')) {
        this.baseUrl = 'https://assets.bcbsnc.com/assets/email'
      } else {
        this.baseUrl = ''
      }
    }
    this.updateBrochureLink()
    this._resizeHandler = () => this.syncPlanHeaderRowHeights()
    window.addEventListener('resize', this._resizeHandler)
  }

  private updateBrochureLink() {
    this.brochureLink =
      getPlanYear(this.shopperContextValue?.formData?.startDate) === '2027'
        ? withBase('/images/U35521_2027_Dental_Vision_Combo_Brochure_508.pdf')
        : this.baseUrl +
          '/2026/U35521_Dental_Vision_combo_brochure_for_individual_Final_508.pdf'
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    window.removeEventListener('resize', this._resizeHandler)
  }
  private _resizeHandler: () => void = () => {}

  get isMobile() {
    return (
      this.breakpoint === BREAKPOINT.PHONE ||
      this.breakpoint === BREAKPOINT.TABLET
    )
  }

  private get isReadOnlyMode() {
    return isReadOnlyFromUrl()
  }

  private get hasDentalPlans() {
    const quote = this.shopperContextValue?.quoteData
    const dentalPlans = quote?.crossSalePlans || quote?.jsonPlans || []
    return dentalPlans.length > 0
  }

  private get hasVisionPlans() {
    const visionPlans =
      this.shopperContextValue?.quoteData?.individualPlans || []
    return visionPlans.length > 0
  }

  private get disableVisionActionButton() {
    return shouldDisablePlanAction({
      readOnly: this.isReadOnlyMode,
      product: 'vision',
      hasDentalPlans: this.hasDentalPlans,
      hasVisionPlans: this.hasVisionPlans,
    })
  }

  // Removed unused topPickChip to satisfy TypeScript noUnusedLocals
  // cost breakdown moved to shared util

  // Removed unused getEnrollButton to satisfy TypeScript noUnusedLocals

  // Removed unused getRecommendButton to satisfy TypeScript noUnusedLocals

  // Removed unused getKeepPlanButton to satisfy TypeScript noUnusedLocals

  // Removed unused getSelectNewButton to satisfy TypeScript noUnusedLocals

  // Removed unused getActionButton to satisfy TypeScript noUnusedLocals
  async firstUpdated() {
    await this.updateComplete
    this.syncPlanHeaderRowHeights()
  }

  async updated(changedProps: Map<string | number | symbol, unknown>) {
    if (changedProps.has('shopperContextValue')) {
      this.updateBrochureLink()
    }
    await this.updateComplete
    this.syncPlanHeaderRowHeights()
  }

  private syncPlanHeaderRowHeights() {
    const headerRows = this.renderRoot.querySelectorAll('.plan-header-row')
    let maxHeight = 0

    headerRows.forEach((row) => {
      ;(row as HTMLElement).style.minHeight = ''
      ;(row as HTMLElement).style.height = ''
    })

    headerRows.forEach((row) => {
      const height = (row as HTMLElement).offsetHeight
      if (height > maxHeight) {
        maxHeight = height
      }
    })

    headerRows.forEach((row) => {
      ;(row as HTMLElement).style.minHeight = `${maxHeight}px`
      ;(row as HTMLElement).style.height = `${maxHeight}px`
    })
  }
  private renderPlanHeader(plan: any, index: number) {
    const isImmediateBinder =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    const tooltipPosition = this.isMobile
      ? 'top'
      : index === 0
        ? 'right'
        : 'left'
    return html`<th>
      <div class="grid-container${!this.isShopper ? ' is-shopper-false' : ''}">
        <div class="grid-item plan-header-row">
          <lh-image
            src="${new URL('../../images/vision_blue.png', import.meta.url)
              .href}"
            alt="${plan.planName}"
          ></lh-image>
          <lh-typography
            variant="p"
            size=${this.isMobile && !this.isShopper ? 'text-sm' : 'text-lg'}
            ><strong>${plan.description}</strong></lh-typography
          >
        </div>
        <div class="grid-item">
          <!--desktop details -->
          <div class="price">
            <div class="costs">
              <lh-typography size="text-sm" alignment="center"
                >Cost per month</lh-typography
              >
              <div class=${classMap({ 'full-width': this.isMobile })}>
                ${isImmediateBinder
                  ? html`<lh-typography type="heading" alignment="center">
                      ${formatCurrency(plan.planRate?.monthlyPremium || 0)}
                    </lh-typography>`
                  : html`<rq-tooltip
                      .description=${getCostBreakdownDescription(
                        plan,
                        this.shopperContextValue?.quoteData,
                        !!this.shopperContextValue?.isAgent
                      )}
                      position=${tooltipPosition}
                    >
                      <lh-typography type="heading" alignment="center">
                        ${formatCurrency(plan.planRate?.monthlyPremium || 0)}
                      </lh-typography>
                    </rq-tooltip>`}
              </div>
            </div>
          </div>
        </div>
        <div class="grid-item">
          <div class="price-buttons">
            ${this.shopperContextValue?.formData?.deliveryMethod === 'e'
              ? html`<lh-button
                  variant="filled"
                  size="md"
                  full-width
                  icon="seal-check"
                  icon-position="left"
                  ?disabled=${this.disableVisionActionButton}
                  @click=${() => this.handleRecommendedPlan(plan.id)}
                  analytics-id="ancillary-rate-quote:vision-compare:recommend-plan:button-text"
                >
                  Recommend Plan
                </lh-button>`
              : html`<lh-button
                  variant="filled"
                  size="md"
                  icon-position="left"
                  full-width
                  ?disabled=${this.disableVisionActionButton}
                  icon="note-pencil"
                  analytics-id="ancillary-rate-quote:vision-compare:choose-plan:button-text"
                  @click=${() => this.handleSelectPlan(plan.id)}
                >
                  Choose Plan
                </lh-button>`}
          </div>
        </div>
      </div>
    </th>`
  }

  private renderFeatureRow(feature: Feature) {
    // Only show features marked for compare view
    if (feature.compare !== 'y') {
      return nothing
    }

    // Check if feature.planCode matches any plan's lob in sampleResults
    const plans = this.shopperContextValue?.quoteData?.individualPlans ?? []
    const hasMatchingPlan = plans.some(
      (plan: any) => !feature.planCode || plan.lob === feature.planCode
    )

    // Only render the row if there's a matching plan
    if (!hasMatchingPlan) {
      return nothing
    }

    // Also check if feature.planName matches any plan in sampleResults
    const hasMatchingPlanName = plans.some(
      (plan: any) => !feature.planName || plan.id === feature.planName
    )

    // Only render the row if there's a matching planName (if specified)
    if (feature.planName && !hasMatchingPlanName) {
      return nothing
    }

    // Build ordered list: recommended first, then others by monthly premium ascending
    // Build ordered list using shared utility
    const ordered = orderVisionPlans(plans)

    return html`<tr>
      <td>
        <lh-typography
          variant="p"
          size=${this.isMobile ? 'text-sm' : 'text-lg'}
        >
          <span class="align-items-4">
            <strong
              >${typeof feature.display === 'string'
                ? unsafeHTML(feature.display)
                : feature.display}</strong
            >
          </span>
        </lh-typography>
      </td>
      ${ordered.map((plan: any) => this.renderFeatureCell(feature, plan))}
    </tr>`
  }

  private renderFeatureCell(feature: Feature, plan: any) {
    // Check if feature.planCode matches this plan's lob
    if (feature.planCode && plan.lob !== feature.planCode) {
      return html`<td>
        <lh-typography>—</lh-typography>
      </td>`
    }

    // Only show the cell if feature.planName matches this plan's id
    // or if feature.planName is not defined (show for all plans with matching planCode)
    if (feature.planName && plan.id !== feature.planName) {
      return html`<td>
        <lh-typography>—</lh-typography>
      </td>`
    }

    const value = plan.details?.[feature.key] ?? plan[feature.key as keyof any]

    // Default case for other features; allow HTML like <sup> in string values
    return html`<td>
      <lh-typography>
        ${value == null
          ? 'Not Covered'
          : typeof value === 'string'
            ? unsafeHTML(value)
            : value}
      </lh-typography>
    </td>`
  }

  private handleSelectPlan(planId: string) {
    // For both shopper and agent flows, centralize behavior in apply()
    this.apply(planId)
  }

  private getPlanMonthlyRate(plan: any): string {
    const rawRate = Array.isArray(plan?.planRate)
      ? plan?.planRate?.[0]?.monthlyPremium
      : plan?.planRate?.monthlyPremium

    if (rawRate === undefined || rawRate === null || rawRate === '') {
      return ''
    }

    return String(rawRate)
  }

  private submitImmediateBinderForm(visionPlanId: string): boolean {
    const quote = this.shopperContextValue?.quoteData
    const visionPlan = (quote?.individualPlans || []).find(
      (p: any) => p.id === visionPlanId || p.planId === visionPlanId
    )

    if (!visionPlan) {
      console.error('Vision plan not found for immediate-binder:', visionPlanId)
      return false
    }

    const dentalPlanId = sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    const dentalSelected =
      sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_SELECTED) === 'true' &&
      !!dentalPlanId
    const dentalPlan = dentalSelected
      ? (quote?.crossSalePlans || quote?.jsonPlans || []).find(
          (p: any) => p.planId === dentalPlanId || p.id === dentalPlanId
        )
      : null

    const form = document.createElement('form')
    form.method = 'post'
    form.action = `${import.meta.env.VITE_BCBSNC_URL}/sapps/buyonline/application/IBinderPayment_ancillaryApplicants.action`
    form.style.display = 'none'

    const appendHiddenInput = (name: string, id: string, value: string) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.id = id
      input.value = value
      form.appendChild(input)
    }

    appendHiddenInput(
      'immediateBinderPayment.isDental',
      'isDental',
      dentalPlan ? 'true' : 'false'
    )
    appendHiddenInput('immediateBinderPayment.isVision', 'isVision', 'true')
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planName',
      'dentalPlan',
      String(dentalPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planName',
      'visionPLan',
      String(visionPlan?.description || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planRate',
      'dentalRate',
      this.getPlanMonthlyRate(dentalPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planRate',
      'visionRate',
      this.getPlanMonthlyRate(visionPlan)
    )
    appendHiddenInput(
      'immediateBinderPayment.iBDentalApp.planID',
      'dentalPlanID',
      String((dentalPlan as any)?.id || dentalPlan?.planId || '')
    )
    appendHiddenInput(
      'immediateBinderPayment.iBVisionApp.planID',
      'visionPlanID',
      String(visionPlan?.id || visionPlanId)
    )

    document.body.appendChild(form)
    form.submit()
    return true
  }

  private handleRecommendedPlan(planId: string) {
    const quote = this.shopperContextValue?.quoteData
    const plans = quote?.individualPlans || []
    const idx = plans.findIndex(
      (p: any) => p.id === planId || p.planId === planId
    )
    if (idx < 0) return
    plans.forEach((p: any) => (p.producerRecommended = false))
    const selected = plans[idx]
    ;(selected as any).producerRecommended = true
    plans.splice(idx, 1)
    plans.unshift(selected)
    this.requestUpdate()
    // Ask parent to switch to List View tab (no route change)
    this.dispatchEvent(
      new CustomEvent('switch-to-list', { bubbles: true, composed: true })
    )
    // Trigger global update so results view re-renders and highlights.
    // Include the current quoteData in event detail to avoid clearing context.
    document.dispatchEvent(
      new CustomEvent('quote-data-updated', {
        detail: { quoteData: this.shopperContextValue?.quoteData },
      })
    )
  }

  private apply(planId: string) {
    // Update plan in result
    this.updatePlan(planId)
    // Persist vision selection
    try {
      sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_SELECTED, 'true')
      sessionStorage.setItem(STORAGE_KEY.VISION_PLAN_ID, planId)
    } catch {
      // non-blocking
    }

    const isImmediateBinderFlow =
      new URLSearchParams(window.location.search).get('view') ===
      'immediate-binder'
    if (isImmediateBinderFlow) {
      this.submitImmediateBinderForm(planId)
      return
    }

    // If a dental plan was previously selected (from dental page), submit both now
    const dentalSelected =
      sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_SELECTED) === 'true'
    const dentalPlanId = sessionStorage.getItem(STORAGE_KEY.DENTAL_PLAN_ID)
    if (dentalSelected && dentalPlanId) {
      const quote = this.shopperContextValue?.quoteData
      const visionPlan = (quote?.individualPlans || []).find(
        (p: any) => p.id === planId || p.planId === planId
      )
      const dentalPlan = (quote?.crossSalePlans || quote?.jsonPlans || []).find(
        (p: any) => p.planId === dentalPlanId || p.id === dentalPlanId
      )

      // If dental selection exists but the dental plan is not loaded
      // (e.g., after navigating back and re-running vision-only),
      // fallback to submitting vision-only and clear stale dental keys.
      if (!visionPlan || !dentalPlan) {
        if (!visionPlan) {
          console.error(
            'Vision plan not found; submitting vision-only: ' + planId
          )
        }
        if (!dentalPlan) {
          console.warn(
            'Dental plan not found; clearing stale dental selection: ' +
              dentalPlanId
          )
          clearSelections({ dental: true })
        }
        this.doApply(planId, true)
        return
      }

      const shoppingCart = [
        { planId: visionPlan.id, plan: visionPlan },
        { planId: dentalPlan.planId, plan: dentalPlan },
      ]

      ApplicationService.submitApplication(
        quote,
        shoppingCart,
        false, // skipVision
        false, // skipDental
        '',
        this.shopperContextValue?.producerSession || null
      )

      // Clean up session selections
      clearSelections({ vision: true, dental: true })
      return
    }

    // Submit immediately with vision only when dental hasn't been selected
    this.doApply(planId, true)
  }

  private updatePlan(planId: string) {
    const plans = this.shopperContextValue?.quoteData?.individualPlans || []
    for (const plan of plans) {
      if (plan.id === planId) {
        // Update applicant rates if needed
        if (
          plan.applicantRate &&
          this.shopperContextValue?.quoteData?.quoteApplication?.applicants
        ) {
          plan.applicantRate.forEach((rate: any, index: number) => {
            if (
              this.shopperContextValue?.quoteData?.quoteApplication.applicants[
                index
              ]
            ) {
              this.shopperContextValue.quoteData.quoteApplication.applicants[
                index
              ].individualRate = rate.monthlyPremium
            }
          })
        }
        break
      }
    }
  }

  private doApply(planId: string, visionOnly: boolean = false) {
    // Add selected plan to shopping cart
    const plan = this.shopperContextValue?.quoteData?.individualPlans?.find(
      (p: any) => p.id === planId || p.planId === planId
    )

    if (!plan) {
      console.error('Plan not found:', planId)
      return
    }

    const shoppingCart = [
      {
        planId: plan.id,
        plan: plan,
      },
    ]

    // Submit application
    ApplicationService.submitApplication(
      this.shopperContextValue?.quoteData,
      shoppingCart,
      false, // skipVision
      visionOnly, // skipDental when submitting vision-only
      '', // campaignId
      this.shopperContextValue?.producerSession || null
    )
    clearSelections({ vision: true })
  }

  render() {
    // Determine the correct year for the info text
    // const effectiveYear = this.inputResponse?.effectiveStart?.split('/')?.pop()
    //  const surveyYear = effectiveYear === '2025' ? '2024' : '2025'
    return html`
      <lh-container
        theme="white"
        content-width="xxl"
        gutters="none"
        context-id="vision-compare"
      >
        <lh-container gutters="none" margin-bottom="md">
          <lh-typography
            >Use the
            <a
              href="https://www.eyemedvisioncare.com/bcbsnc/public/provloc.emvc?networkId=108&amp;nwDDDisabled=true"
              target="_blank"
              >Find a Vision Provider tool<lh-icon
                size="md"
                name="arrow-square-out"
              ></lh-icon
            ></a>
            to search the provider network before choosing a plan.
          </lh-typography>
          <lh-typography>
            <strong>Note:</strong> Applicants who have had Blue 20/20 coverage
            within the past 12 months will be removed from this application if
            they apply for Blue 20/20 for Individuals coverage.
          </lh-typography>
        </lh-container>
        <!-- Add a scrollable container -->
        <div class="table-container">
          <table class="compare-table">
            <tbody>
              <tr>
                <td class="align-middle">
                  <lh-typography
                    variant="p"
                    size=${this.isMobile ? 'text-sm' : 'text-lg'}
                    ><strong>Plan Name</strong></lh-typography
                  >
                </td>
                ${(() => {
                  const plans =
                    this.shopperContextValue?.quoteData?.individualPlans ?? []
                  const ordered = orderVisionPlans(plans)
                  return ordered.map((plan: any, index: number) =>
                    this.renderPlanHeader(plan, index)
                  )
                })()}
              </tr>
            </tbody>
            <tbody>
              ${this.features.map((feature) => this.renderFeatureRow(feature))}
            </tbody>
          </table>
        </div>
        <!-- Plans Compare Info Text -->
        <lh-container
          padding-top="lg"
          content-width="xxl"
          alignment="left"
          gutters="sm"
        >
          <lh-typography type="text" size="md">
            <sup>1</sup> One benefit every 12 months for either lenses or
            contact lenses. Please see
            <a href="${this.brochureLink}" target="_blank">Product Brochure</a>
            for details.
          </lh-typography>
        </lh-container>
      </lh-container>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'rq-marketplace-compare': MarketplaceCompare
  }
}
